import React from 'react';
import { Link } from 'react-router-dom';
import { Heading } from '@mm/components';
import { CheckmarkIcon, ReloadIcon } from '@mm/icons';
import * as allPermissions from '@mm/constants/facebook-permissions';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { useFacebookSDKActions } from '@mm/state/actions/facebookActions';
import { selectInstagramAccountId } from '@mm/state/selectors/instagramSelectors';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { SETTINGS_MAINTENANCE } from '../../constants/routes';
import { NeedHelpLabel } from '../../../editor/features/common/components/NeedHelpLabel/NeedHelpLabel';
import { ClearOutlined } from '@ant-design/icons';

export enum ErrorNumber {
  tokenException = 10,
  permissionsException = 12,
  IGMessagingException = 14,
  primaryReceiverException = 15,
  FBMessagingException = 20,
  CGException = 30,
  badContentException = 40,
  rateLimitsException = 60,
  CGRateLimitsException = 61,
  campaignRateLimitsException = 62,
  IGRateLimitsException = 63,
  disbledMessagingException = 80,
}

export enum IGErrorCodes {
  notIGBusiness = 10,
  noPageConnectedtoIG = 100,
  disbaledThirdPartyMessages = 200,
}

export enum ErrorSubCodes {
  IGAccountReviewRequired = 33,
}

type Props = {
  pagination: number;
  companyId: number;
  pageId: number;
  errorId: number;
  onClick: Function;
  deleteFacebookError: Function;
  getConnectedPages: Function;
  getConnectedPage: typeof connectedPageCRUDActions.retrievePage;
  chatbotId: number;
};
const NEED_HELP_COLOR = '#262626';
const NEED_HELP_STYLES = { margin: '0px 5px' };

const TOKEN_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Cannot send messages to Facebook because Facebook token is expired. Click
    Refresh to update it.
    <NeedHelpLabel
      iconOnly
      link="https://mobilemonkey.com/help/article/isqurvcb0l-refresh-page-access-token-error"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);

const PAGE_PERMISSIONS_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Cannot send messages to Facebook. Please update the page permissions.
    <NeedHelpLabel
      iconOnly
      link="https://mobilemonkey.com/help/article/dqy287mcgy-edit-your-facebook-permissions"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);

const TOKEN_EXCEPTION_ACTION = ({
  chatbotId,
  deleteFacebookError,
  pageId,
  errorId,
}: Props) => {
  const url = SETTINGS_MAINTENANCE(chatbotId);
  return (
    <div className="facebook-errors__actions">
      <Link to={url} className="facebook-errors__link">
        <PrimaryButton className="facebook-errors__button">
          <ReloadIcon className="facebook-errors__reload-icon" />
          Refresh
        </PrimaryButton>
      </Link>
      <PrimaryButton
        onClick={async () => {
          deleteFacebookError(pageId, errorId);
        }}
        className="facebook-errors__button secondary"
        color="secondary-blue"
      >
        <CheckmarkIcon className="facebook-errors__checkmark-icon" />I already
        fixed it
      </PrimaryButton>
    </div>
  );
};

const PAGE_PERMISSIONS_EXCEPTION_ACTION = ({
  deleteFacebookError,
  pageId,
  errorId,
}: Props) => {
  const { requestPermissionsWithManualFlowFallback } = useFacebookSDKActions();
  const instagramAccountId = useSelector(selectInstagramAccountId);
  const permissions = instagramAccountId
    ? allPermissions.getArrayOfPermissions(allPermissions.IG_SIGNUP_PERMISSIONS)
    : allPermissions.getArrayOfPermissions(allPermissions.ALL_PERMISSIONS);
  return (
    <div className="facebook-errors__actions">
      <PrimaryButton
        onClick={async () => {
          try {
            await requestPermissionsWithManualFlowFallback(permissions);
            await deleteFacebookError(pageId, errorId);
          } catch (err) {}
        }}
        className="facebook-login-button errors"
      >
        <i className="facebook-login-button__icon error" aria-hidden="true" />
        Update permissions
      </PrimaryButton>
      <PrimaryButton
        onClick={async () => {
          deleteFacebookError(pageId, errorId);
        }}
        color="secondary-blue"
      >
        <CheckmarkIcon className="facebook-errors__checkmark-icon" />I already
        fixed it
      </PrimaryButton>
    </div>
  );
};

export const MESSAGES_EXCEPTION_ACTION = ({
  deleteFacebookError,
  pageId,
  errorId,
  getConnectedPage,
}: Props) => (
  <div>
    <PrimaryButton
      onClick={async () => {
        await deleteFacebookError(pageId, errorId, true);
        getConnectedPage(pageId);
      }}
      className="facebook-errors__message-action"
    >
      <ClearOutlined className="facebook-errors__reload-icon" />
      Clear
    </PrimaryButton>
  </div>
);

const BAD_CONTENT_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Cannot send some of your page messages because you shared something that
    isn&apos;t allowed on Facebook
  </div>
);

const CG_ACTIVITY_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Could not send some of your Comment Guard messages because another service
    is monitoring your Facebook post.
  </div>
);

const TOO_FREQUENT_MESSAGING_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Facebook has temporarily blocked your message sending because messages are
    being sent too frequently
  </div>
);

const TOO_FREQUENT_MESSAGING_CG_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Facebook has temporarily blocked your Comment Guard functionality because
    messages are being sent too frequently
  </div>
);

const TOO_FREQUENT_MESSAGING_CAMPAIGN_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Facebook has temporarily blocked your Campaign functionality because
    messages are being sent too frequently
  </div>
);

const TOO_FREQUENT_MESSAGING_IG_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Instagram has temporarily blocked your message sending because messages are
    being sent too frequently
  </div>
);

const MESSAGES_DISABLED_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Your Facebook page has messages disabled in the settings
  </div>
);

const CG_ACTIVITY_EXCEPTION_ACTION = ({
  deleteFacebookError,
  pageId,
  errorId,
  getConnectedPages,
  companyId,
  pagination,
}: Props) => (
  <div>
    <PrimaryButton
      onClick={() => {
        deleteFacebookError(pageId, errorId);
        getConnectedPages(companyId, pagination);
      }}
    >
      <CheckmarkIcon className="facebook-errors__checkmark-icon" />
      Fix it
    </PrimaryButton>
  </div>
);

const PPRIMARY_RECEIVER_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    MobileMonkey is not the primary receiver for your page.
    <NeedHelpLabel
      iconOnly
      link="https://mobilemonkey.com/help/l/en/article/t40ow0g1bz-make-mobile-monkey-a-primary-receiver"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);
const FB_MESSAGING_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Some of your page messages are blocked by Facebook. Click Clear to reset the
    error.
  </div>
);

const IG_ACCOUNT_REVIEW_REQUIRED_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Review the connection between Facebook page and the Instagram account.
    <NeedHelpLabel
      iconOnly
      link="https://mobilemonkey.com/help/l/en/article/fjww6gzn5p-review-connection-with-the-instagram-account"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);

const DISABLED_IG_THIRD_PARTY_MESSAGES_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Instagram partner messages are disabled on your Instagram account.
    <NeedHelpLabel
      iconOnly
      link="https://mobilemonkey.com/help/article/vq42z251fw-enable-access-to-instagram-partner-messages"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);

const IG_ACCOUNT_IS_NOT_BUSINESS_TYPE_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    Instagram account is not a business type account.
    <NeedHelpLabel
      iconOnly
      link="https://www.facebook.com/business/help/502981923235522"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);

const BANNED_OR_DISCONNECT_IG_ACCOUNT_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    The facebook page is not linked to an Instagram account or the business has
    been blocked from sending messages via the IG Messaging API.
    <NeedHelpLabel
      iconOnly
      link="https://www.facebook.com/business/help/345675946300334?id=419087378825961"
      style={NEED_HELP_STYLES}
      color={NEED_HELP_COLOR}
    />
  </div>
);

const SEND_IG_MESSAGING_FAILED_EXCEPTION_BODY = (
  <div className="facebook-errors__error-body">
    An error occured while sending message on the Instagram. Click Clear to
    reset the error.
  </div>
);

export const BOT_DO_NOT_WORK_HEADER = (
  <Heading
    text="Your Chatbot doesn't work"
    size={3}
    className="facebook-errors__error-header"
  />
);

export const INSTAGRAM_DO_NOT_WORK_HEADER = (
  <Heading
    text="Instagram Chat Starters don’t work"
    size={3}
    className="facebook-errors__error-header"
  />
);

export const TEMPORARY_ISSUE_HEADER = (
  <Heading
    text="Your Chatbot may have problems if this issue isn't resolved"
    size={3}
    className="facebook-errors__error-header--temporary-issue"
  />
);

export const IGNORED_ERROR_TYPES = [
  {
    code: '100',
    subCode: '33',
    number: 16,
  },
];

export const ERROR_TYPES = {
  [ErrorNumber.tokenException]: {
    body: TOKEN_EXCEPTION_BODY,
    action: TOKEN_EXCEPTION_ACTION,
    header: BOT_DO_NOT_WORK_HEADER,
  },
  [ErrorNumber.permissionsException]: {
    body: PAGE_PERMISSIONS_EXCEPTION_BODY,
    action: PAGE_PERMISSIONS_EXCEPTION_ACTION,
    header: BOT_DO_NOT_WORK_HEADER,
  },
  [ErrorNumber.IGMessagingException]: {
    body: SEND_IG_MESSAGING_FAILED_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: INSTAGRAM_DO_NOT_WORK_HEADER,

    [IGErrorCodes.notIGBusiness]: {
      body: IG_ACCOUNT_IS_NOT_BUSINESS_TYPE_EXCEPTION_BODY,
      action: MESSAGES_EXCEPTION_ACTION,
      header: INSTAGRAM_DO_NOT_WORK_HEADER,
    },
    [IGErrorCodes.noPageConnectedtoIG]: {
      body: BANNED_OR_DISCONNECT_IG_ACCOUNT_EXCEPTION_BODY,
      action: MESSAGES_EXCEPTION_ACTION,
      header: INSTAGRAM_DO_NOT_WORK_HEADER,
    },
    [IGErrorCodes.disbaledThirdPartyMessages]: {
      body: DISABLED_IG_THIRD_PARTY_MESSAGES_EXCEPTION_BODY,
      action: MESSAGES_EXCEPTION_ACTION,
      header: INSTAGRAM_DO_NOT_WORK_HEADER,
    },
  },
  [ErrorNumber.primaryReceiverException]: {
    body: PPRIMARY_RECEIVER_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.FBMessagingException]: {
    body: FB_MESSAGING_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,

    [IGErrorCodes.noPageConnectedtoIG]: {
      [ErrorSubCodes.IGAccountReviewRequired]: {
        body: IG_ACCOUNT_REVIEW_REQUIRED_EXCEPTION_BODY,
        action: MESSAGES_EXCEPTION_ACTION,
        header: INSTAGRAM_DO_NOT_WORK_HEADER,
      },
    },
  },
  [ErrorNumber.CGException]: {
    body: CG_ACTIVITY_EXCEPTION_BODY,
    action: CG_ACTIVITY_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.badContentException]: {
    body: BAD_CONTENT_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.rateLimitsException]: {
    body: TOO_FREQUENT_MESSAGING_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.CGRateLimitsException]: {
    body: TOO_FREQUENT_MESSAGING_CG_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.campaignRateLimitsException]: {
    body: TOO_FREQUENT_MESSAGING_CAMPAIGN_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.IGRateLimitsException]: {
    body: TOO_FREQUENT_MESSAGING_IG_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: TEMPORARY_ISSUE_HEADER,
  },
  [ErrorNumber.disbledMessagingException]: {
    body: MESSAGES_DISABLED_EXCEPTION_BODY,
    action: MESSAGES_EXCEPTION_ACTION,
    header: BOT_DO_NOT_WORK_HEADER,
  },
};
